<template>
	<v-card color="rgba(41, 41, 47, 0.9)">
		<v-card-title class="d-flex align-center justify-space-between">
			仮設道路AI (prototype / PoC)
			<v-btn icon variant="flat" @click="close">
				<v-icon>mdi-close</v-icon>
			</v-btn>
		</v-card-title>
		<v-card-text>
			<v-row>
				<!-- 始点・終点 -->
				<v-col cols="12" sm="4">
					<div class="d-flex align-center mb-2">
						<span class="text-subtitle-1 mr-4">始点・終点</span>
						<v-btn color="primary" :loading="picking" @click="pickPoints">
							2点を選択
						</v-btn>
					</div>
					<v-text-field label="始点 X" v-model="startText.x" variant="underlined" readonly density="compact" />
					<v-text-field label="始点 Y" v-model="startText.y" variant="underlined" readonly density="compact" />
					<v-text-field label="始点 Z" v-model="startText.z" variant="underlined" readonly density="compact" />
					<v-text-field label="終点 X" v-model="endText.x" variant="underlined" readonly density="compact" />
					<v-text-field label="終点 Y" v-model="endText.y" variant="underlined" readonly density="compact" />
					<v-text-field label="終点 Z" v-model="endText.z" variant="underlined" readonly density="compact" />
				</v-col>

				<!-- パラメータ -->
				<v-col cols="12" sm="4">
					<span class="text-subtitle-1">パラメータ</span>
					<v-text-field label="道路幅員 [m]" v-model.number="roadWidthM" type="number" variant="outlined"
						density="compact" />
					<v-text-field label="法面勾配比 (1:n)" v-model.number="slopeRatio" type="number" variant="outlined"
						density="compact" />
					<v-text-field label="最大縦断勾配 [%]" v-model.number="maxSlopePercent" type="number" variant="outlined"
						density="compact" />
					<v-select label="目的関数" v-model="objectives" :items="objectiveItems" multiple chips variant="outlined"
						density="compact" />
					<v-select label="cf_diff 評価方式" v-model="cfDiffEvaluator" :items="cfDiffEvaluatorItems" variant="outlined"
						density="compact" />
					<v-text-field label="目的ごとの候補数 (top_k)" v-model.number="topKPerObjective" type="number" variant="outlined"
						density="compact" />
					<v-select label="探索バックエンド" v-model="searchBackend" :items="searchBackendItems" variant="outlined"
						density="compact" />
					<v-text-field label="目的項の重み (objective_weight)" v-model.number="objectiveWeight" type="number"
						variant="outlined" density="compact" hint="大きいほど目的別の差分が強くなる (既定 1.0)" persistent-hint />
				</v-col>

				<!-- 候補結果 -->
				<v-col cols="12" sm="4">
					<div class="d-flex align-center justify-space-between mb-2">
						<span class="text-subtitle-1">候補経路</span>
						<v-btn color="success" :disabled="!hasPoints" :loading="running" @click="run">
							実行
						</v-btn>
					</div>
					<v-alert v-if="errorMessage" type="error" density="compact" class="mb-2">
						{{ errorMessage }}
					</v-alert>
					<v-table v-if="candidates.length" density="compact">
						<thead>
							<tr>
								<th>目的</th>
								<th>点数</th>
								<th v-for="k in metricKeys" :key="k">{{ k }}</th>
								<th>状態</th>
								<th></th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="(c, i) in candidates" :key="i">
								<td>{{ c.objective }}</td>
								<td>{{ c.node_count }}</td>
								<td v-for="k in metricKeys" :key="k">{{ formatMetric(c, k) }}</td>
								<td>
									<v-tooltip v-if="c.is_fallback" location="top">
										<template #activator="{ props }">
											<v-chip v-bind="props" size="x-small" color="warning" variant="tonal">
												裁量(違反あり)
											</v-chip>
										</template>
										<span>{{ violationSummary(c) }}</span>
									</v-tooltip>
									<v-chip v-else size="x-small" color="success" variant="tonal">適合</v-chip>
								</td>
								<td>
									<v-btn size="small" color="primary" :loading="renderingIndex === i" @click="render(i)">
										描画
									</v-btn>
								</td>
							</tr>
						</tbody>
					</v-table>
					<div v-else-if="!running" class="text-caption">
						実行すると候補が表示されます。
					</div>

					<!-- 診断（見つからなかった理由） -->
					<div v-if="describedDiagnostics.length" class="mt-3">
						<div class="text-subtitle-2 mb-1">
							{{ candidates.length ? "診断情報" : "経路が見つからなかった理由" }}
						</div>
						<v-alert v-for="(d, i) in describedDiagnostics" :key="i" :type="d.level" variant="tonal"
							density="compact" class="mb-1">
							<div class="font-weight-medium">{{ d.title }}</div>
							<div v-if="d.detail" class="text-caption">{{ d.detail }}</div>
							<div v-if="d.hint" class="text-caption">対処: {{ d.hint }}</div>
						</v-alert>
					</div>
				</v-col>
			</v-row>
		</v-card-text>
	</v-card>
</template>

<script>
import { convertCoordinatesToPointCloudCrs } from "@/utils/cesium-common";
import { notifyError, notifyWarning } from "@/utils/notificationUtils";
import {
	renderPrototypeRoad,
	runPrototypeRoadAI,
} from "@/utils/prototypeRoadAIUtils";
import {
	describeDiagnostic,
	formatMetricForMode,
	violationSummary,
} from "@/utils/roadAiDiagnostics";
import { filterObjectivesForMode } from "./road-ai/roadAi.types";
import { inspectVisibleTerrain } from "./road-ai/roadAiDataInspection";
import {
	buildBlockedGuideMessage,
	buildRunErrorMessage,
} from "./road-ai/roadAiMessages";

export default {
	name: "PrototypeRoadAI",
	props: {},
	data: () => ({
		picking: false,
		running: false,
		renderingIndex: -1,
		errorMessage: "",
		start: null,
		end: null,
		roadWidthM: 4.0,
		slopeRatio: 0.1,
		maxSlopePercent: 90.0,
		objectives: ["cut", "fill", "over_cut", "over_fill", "cf_diff"],
		cfDiffEvaluator: "proxy",
		topKPerObjective: 1,
		searchBackend: "hybrid_a_star",
		objectiveWeight: 1.0,
		candidates: [],
		diagnostics: [],
		// 実行に実際に使われたアセット（受付レスポンスの echo）とモード。
		usedSource: null,
		mode: null,
		objectiveItems: ["cut", "fill", "over_cut", "over_fill", "cf_diff"],
		cfDiffEvaluatorItems: ["proxy", "global"],
		searchBackendItems: ["hybrid_a_star", "a_star"],
		metricKeys: ["cut", "fill", "over_cut", "over_fill", "cf_diff"],
	}),
	computed: {
		hasPoints() {
			return Array.isArray(this.start) && Array.isArray(this.end);
		},
		startText() {
			return this.formatPoint(this.start);
		},
		endText() {
			return this.formatPoint(this.end);
		},
		describedDiagnostics() {
			// 診断の日本語化は roadAiDiagnostics.js（提案経路一覧パネルと共通）に委ねる。
			return (this.diagnostics || []).map((d) => describeDiagnostic(d));
		},
	},
	methods: {
		violationSummary,
		close() {
			this.$emit("close", undefined);
		},
		formatPoint(p) {
			if (!Array.isArray(p)) return { x: "", y: "", z: "" };
			return {
				x: p[0].toFixed(3),
				y: p[1].toFixed(3),
				z: p[2].toFixed(3),
			};
		},
		formatMetric(c, key) {
			// 点群のみモードの設計依存指標は「対象外」と表示する（AC-7）。
			return formatMetricForMode(c, key, this.mode).text;
		},
		async pickPoints() {
			this.errorMessage = "";
			this.picking = true;
			// ダイアログを一旦隠してビューアをクリック可能にする（eager なのでインスタンスは保持）。
			this.$emit("close", undefined);
			notifyWarning(this, "始点・終点の2点をビュー上でクリックしてください。");
			try {
				// WGS84 の Cartesian3 を 2 点取得（単独動作。startCreation 不要）。
				const picked = await window.viewer.pickPointsForRoadConstruction_AI(2);
				if (!picked || picked.length < 2) {
					notifyError(this, "2点を選択してください。");
					return;
				}
				// プロジェクト座標へ変換
				const local = await convertCoordinatesToPointCloudCrs(picked);
				if (!local || local.length < 2) {
					notifyError(this, "座標変換に失敗しました。");
					return;
				}
				this.start = [local[0].x, local[0].y, local[0].z];
				this.end = [local[1].x, local[1].y, local[1].z];
				this.candidates = [];
				this.diagnostics = [];
			} catch (e) {
				console.error(e);
				this.errorMessage = e?.message || "点選択に失敗しました。";
			} finally {
				this.picking = false;
				// ダイアログを再表示
				window.dispatchEvent(
					new CustomEvent("openPropertyDialog", {
						detail: { id: 1, type: 100 },
					}),
				);
			}
		},
		async run() {
			if (!this.hasPoints) return;
			// 非同期化で待ち時間が伸びるため多重実行を防ぐ（AC-18 の趣旨をデバッグ導線にも適用）。
			if (this.running) return;
			this.errorMessage = "";

			// 表示中データを検査してモードと使用データを確定する（チャット導線と同一ロジック）。
			const inspection = inspectVisibleTerrain({
				pointClouds: this.$store.getters.visiblePointClouds || [],
				designs: this.$store.getters.visibleDesigns || [],
			});
			if (inspection.status === "blocked") {
				this.errorMessage = buildBlockedGuideMessage(inspection.reason);
				return;
			}
			const selection = inspection.selection;
			const siteId = this.$route.query.siteId;
			if (!siteId) {
				this.errorMessage =
					"現場 ID を取得できないため実行できません（現場ページで開いてください）。";
				return;
			}

			this.mode = selection.mode;
			this.running = true;
			this.candidates = [];
			this.diagnostics = [];
			this.usedSource = null;
			try {
				const data = await runPrototypeRoadAI({
					siteId,
					selection,
					start: this.start,
					end: this.end,
					roadWidthM: this.roadWidthM,
					slopeRatio: this.slopeRatio,
					maxSlopePercent: this.maxSlopePercent,
					objectives: this.effectiveObjectives(selection.mode),
					cfDiffEvaluator: this.cfDiffEvaluator,
					topKPerObjective: this.topKPerObjective,
					searchBackend: this.searchBackend,
					objectiveWeight: this.objectiveWeight,
				});
				this.candidates = data.candidates || [];
				this.diagnostics = data.diagnostics || [];
				this.usedSource = data.usedSource || null;
				if (!this.candidates.length) {
					this.errorMessage =
						"候補経路が見つかりませんでした。下記の理由をご確認ください。";
				}
			} catch (e) {
				console.error(e);
				this.errorMessage = e?.code
					? buildRunErrorMessage(e.code)
					: buildRunErrorMessage("ROUTE_ENGINE_FAILED");
			} finally {
				this.running = false;
			}
		},
		effectiveObjectives(mode) {
			// 点群のみモードでは設計依存の目的を外す（含めると受付で 400 になる。AC-23）。
			// フィルタの単一の正は roadAi.types の filterObjectivesForMode（チャット導線と共通）。
			return filterObjectivesForMode(this.objectives, mode);
		},
		async render(index) {
			const candidate = this.candidates[index];
			if (!candidate || !Array.isArray(candidate.points)) return;
			// 実行に実際に使われた点群アセットへ関連付ける（チャット導線と揃える）。
			// v2-server は asset_id 無しの点群に "pc-<id>" 形式を返し得るため、
			// 数値化できない場合は表示中の点群へフォールバックする。
			const rawAssetId = this.usedSource?.pointCloudAssetId;
			const usedAssetId = rawAssetId ? Number(rawAssetId) : Number.NaN;
			const pointCloudAssetId = Number.isFinite(usedAssetId)
				? usedAssetId
				: this.$store.getters.activePointCloud?.getAssetIdNum();
			if (pointCloudAssetId == null || Number.isNaN(pointCloudAssetId)) {
				notifyError(
					this,
					"点群が選択されていません。点群を表示・選択してから描画してください。",
				);
				return;
			}
			this.renderingIndex = index;
			const roadName = this.buildRoadName(candidate);
			try {
				await renderPrototypeRoad(candidate.points, pointCloudAssetId, (id) => {
					const event = new CustomEvent("openPropertyDialog", {
						detail: { id, type: 0, isAI: true, roadName },
					});
					window.dispatchEvent(event);
				});
				this.close();
			} catch (e) {
				console.error(e);
				this.errorMessage = e?.message || "道路の作成に失敗しました。";
			} finally {
				this.renderingIndex = -1;
			}
		},
		buildRoadName(candidate) {
			// 道路名は validName 制約（()や: 等の記号不可）を満たす英数・_・日本語のみで生成する。
			const pad = (n) => String(n).padStart(2, "0");
			const now = new Date();
			const stamp = `${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
			const objective = candidate?.objective || "route";
			return `仮設道路AI_${objective}_${stamp}`;
		},
	},
};
</script>
